import * as THREE from 'three';

/**
 * Hostile lock-on missile — same grey/white brick language as the player missile,
 * with a more visible yellow/orange thruster flame trail.
 */
const BODY = 0x8a9098;
const BODY_LIGHT = 0xc5c9d0;
const BODY_DARK = 0x4a4f57;
const NOSE = 0xe8eaed;
const FIN = 0x6a717c;
const FLAME_CORE = 0xfff59d;
const FLAME_MID = 0xffd54f;
const FLAME_OUTER = 0xff8f00;

/**
 * Enemy missile. Local +Z is nose-forward before orientation.
 */
export function createEnemyMissile(): THREE.Group {
  const missile = new THREE.Group();
  missile.name = 'arcade-enemy-missile';

  const bodyMat = new THREE.MeshBasicMaterial({ color: BODY });
  const lightMat = new THREE.MeshBasicMaterial({ color: BODY_LIGHT });
  const darkMat = new THREE.MeshBasicMaterial({ color: BODY_DARK });
  const noseMat = new THREE.MeshBasicMaterial({ color: NOSE });
  const finMat = new THREE.MeshBasicMaterial({ color: FIN });
  const flameCoreMat = new THREE.MeshBasicMaterial({
    color: FLAME_CORE,
    transparent: true,
    opacity: 0.95,
  });
  const flameMidMat = new THREE.MeshBasicMaterial({
    color: FLAME_MID,
    transparent: true,
    opacity: 0.85,
  });
  const flameOuterMat = new THREE.MeshBasicMaterial({
    color: FLAME_OUTER,
    transparent: true,
    opacity: 0.7,
  });

  // Grey shaft
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.32, 1.15), bodyMat);
  missile.add(body);

  // Light band / panel detail
  const band = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.34, 0.18), lightMat);
  band.position.z = 0.2;
  missile.add(band);

  // Rear thruster housing
  const thruster = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.24, 0.22), darkMat);
  thruster.position.set(0, 0, -0.62);
  missile.add(thruster);

  // Flame trail (outer → mid → core), behind the thruster (−Z)
  const flameOuter = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.22, 0.42), flameOuterMat);
  flameOuter.position.set(0, 0, -0.95);
  missile.add(flameOuter);
  const flameMid = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.16, 0.36), flameMidMat);
  flameMid.position.set(0, 0, -0.92);
  missile.add(flameMid);
  const flameCore = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, 0.28), flameCoreMat);
  flameCore.position.set(0, 0, -0.88);
  missile.add(flameCore);

  // White/grey nose
  const nose = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.28, 0.36), noseMat);
  nose.position.set(0, 0, 0.68);
  missile.add(nose);
  const tip = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.16, 0.18), lightMat);
  tip.position.set(0, 0, 0.9);
  missile.add(tip);

  // Grey fins
  const finGeo = new THREE.BoxGeometry(0.1, 0.38, 0.26);
  for (const rot of [0, Math.PI / 2]) {
    const fin = new THREE.Mesh(finGeo, finMat);
    fin.rotation.z = rot;
    fin.position.set(0, 0, -0.32);
    missile.add(fin);
  }

  missile.userData.radius = 0.95;
  return missile;
}
